import { lazy, Suspense } from "react";
import { createBrowserRouter, Outlet, redirect } from "react-router";

import AppSettings from "../../appsettings";
import DelayedSpinner from "../../components/atoms/DelayedSpinner";
import { VehicleDetailsProvider } from "../../components/contexts/vehicleDetailsContext.js";
import { browseRouteQueries } from "../../components/molecules/BrowseComponent/browseQueries";
import RouteErrorPage from "../../components/molecules/RouteErrorPage/RouteErrorPage.js";
import queryClient from "../../queryClient";
import { ammPageName } from "../../shared/amm/ammPages";
import { AUTH_ROUTE_PATHS } from "../../services/auth/authRouteConfig";
import { getAuthProvider } from "../../services/auth/providers";
import { documentArchiveQuery } from "../../services/documentArchiveQueries";
import {
  dynamicPageNavQuery,
  dynamicPageQuery,
} from "../../services/dynamicPageQueries";
import { miscPageQuery } from "../../services/miscQueries";
import { getCurrentUser } from "../../services/auth/currentUser";
import { articleRouteQueries } from "../articlesV2/articleQueries";
import {
  XP_FAQ_PAGE,
  XP_MISSIONS_CONTENT_PAGE,
  XP_OVERVIEW_PAGE,
  xpContentQuery,
} from "../engageXP/xpPages";
import { evPageName } from "../ev/evPages";
import { safetyPageName } from "../safetyV2/safetyPages";
import { searchRouteQueries } from "../searchBarV2/searchQueries";
import { towingPayloadPath } from "../towingPayload/towingPayloadRedirect";
import AppShell from "./AppShell";
import {
  authSessionMiddleware,
  requireSignInMiddleware,
} from "./authMiddleware";
import { smartPathPageName } from "../smartPathV2/smartPathPages";
import { useAppShell } from "./AppShellContext";
import PrivateRoute from "./privateRoute";
import {
  hasLiveSession,
  prefetchLoader,
  searchParamsOf,
} from "./routePrefetch";

// Legacy /engageResources/tag/:recordType opens Browse filtered by that tag.
export const engageResourcesRedirectLoader = ({ params }) => {
  const { resourceType, recordType } = params;
  return redirect(
    resourceType === "tag" && recordType
      ? `/browse?tags=${encodeURIComponent(recordType)}`
      : "/browse",
  );
};

const RootLayout = () => (
  <Suspense fallback={<DelayedSpinner />}>
    <Outlet />
  </Suspense>
);

const base = AppSettings.BasePath;

const load =
  (importer, name = "default") =>
  () =>
    importer().then((module) => module[name]);

const load404 = load(() => import("../../components/atoms/404NotFound"));

const pageFallback = <DelayedSpinner />;

// Route-level code splitting: the router fetches a page's chunk before it
// commits the navigation, so the current page stays on screen instead of
// flashing a spinner, and on a cold load the chunk downloads alongside the
// auth bootstrap instead of after it.
//
// Without a live session the page won't render — PrivateRoute is about to send
// the user to sign in — so the chunk is left to React.lazy, which downloads it
// only if the page does render (a session renewed in place). Fetching it anyway
// held up the sign-in redirect behind the page's download and parse.
//
// The hydrate fallback is per page, not on a parent: on a cold load the router
// renders every route above the first unresolved one, so the shell paints
// while the page's chunk, the sign-in check (requireSignInMiddleware) and its
// loaders finish. A parent fallback would hold the whole screen on a spinner.
const resolvePage = async (loadComponent) =>
  hasLiveSession()
    ? loadComponent()
    : lazy(() => loadComponent().then((component) => ({ default: component })));

const page = (importer, { name, render } = {}) => ({
  hydrateFallbackElement: pageFallback,
  lazy: async () => {
    const Page = await resolvePage(load(importer, name));
    return { element: render ? render(Page) : <Page /> };
  },
});

// A sub-page of a section (glossary, my account, Engage XP, product). Its errors
// render in place of the sub-page, so the section's tabs and header stay usable.
const childPage = (importer, options) => ({
  errorElement: <RouteErrorPage />,
  ...page(importer, options),
});

const notFoundPage = {
  hydrateFallbackElement: pageFallback,
  lazy: async () => {
    const NotFound = await resolvePage(load404);
    return { element: <NotFound display /> };
  },
};

const loginRoute = {
  hydrateFallbackElement: pageFallback,
  lazy: async () => {
    const PageLogin = await load(getAuthProvider().loadLoginPage)();
    const LoginRoute = () => {
      const { userManager } = useAppShell();
      return <PageLogin userManager={userManager} />;
    };
    return { Component: LoginRoute };
  },
};

// Each provider names the page that reads its authorization response (see
// loadCallbackPage): it arrives in the query string or the fragment depending
// on the provider's responseMode.
const authCallbackRoute = {
  hydrateFallbackElement: pageFallback,
  lazy: async () => {
    const Callback = await load(getAuthProvider().loadCallbackPage)();
    const AuthCallbackRoute = () => {
      const { userManager } = useAppShell();
      return <Callback userManager={userManager} />;
    };
    return { Component: AuthCallbackRoute };
  },
};

const ProtectedLayout = () => (
  <PrivateRoute>
    <Outlet />
  </PrivateRoute>
);

// The two requests a /pages/:category/:name page makes: its tabs and its content.
const dynamicPageQueries = (category, name) => [
  dynamicPageNavQuery(category),
  dynamicPageQuery(category, name),
];

// Legacy URLs that <Navigate> on to a /pages route. The redirect stays in the
// component: loaders never see the URL's #hash, so a loader redirect would drop
// the fragment that bookmarks like /ev/bev-basics#plug-types depend on. The
// loader starts the target page's data while the redirect's chunk loads.
const legacyDynamicPageLoader = (category, pageNameFor) =>
  prefetchLoader(({ page }) => dynamicPageQueries(category, pageNameFor(page)));

// /pages/:category has no content of its own: it opens the category's first tab.
// Redirecting here skips rendering an empty page just to navigate again. Without
// a session, or if the nav request fails, the component takes over as before.
export const categoryRedirectLoader = async ({ params }) => {
  const category = params.category?.toLowerCase();
  if (!category || !hasLiveSession()) {
    return null;
  }
  try {
    const nav = await queryClient.ensureQueryData(
      dynamicPageNavQuery(category),
    );
    const firstTab = nav?.tabs?.[0]?.name;
    return firstTab ? redirect(`${base}/pages/${category}/${firstTab}`) : null;
  } catch {
    return null;
  }
};

// Query definitions whose services aren't otherwise in the entry chunk, loaded
// the first time a loader needs them.
const glossaryQueries = () => import("../glossary/glossaryQueries");
const profileQueries = () => import("../profilePage/profileQueries");
const xpQueries = () => import("../engageXP/xpQueries");

// A quiz opened from the URL. On the tab routes rather than the layout, whose
// loader doesn't rerun when only the quiz in the URL changes.
const xpQuizLoader = prefetchLoader(({ quizId }) =>
  quizId ? xpQueries().then((q) => [q.xpQuizQuery(quizId)]) : [],
);

const vpPrefetch = () => import("../VPRouter/vpRoutePrefetch");

// The year's data, rerun on every product page (the layout's loader only
// reruns when the vehicle changes).
const vpYearLoader = prefetchLoader((params) =>
  vpPrefetch().then((vp) => vp.vpYearQueries(params)),
);

// A product page's component depends on its model year, so it comes from the
// year's route map (routesConfig) rather than the route table. Waiting for it
// here, as `lazy` does for other pages, keeps the current page on screen
// instead of flashing a skeleton while the new one downloads.
const vpPageLoader = async (args) => {
  vpYearLoader(args);
  if (!hasLiveSession()) {
    return null;
  }
  try {
    const { preloadVpPage } = await vpPrefetch();
    await preloadVpPage(args.params);
  } catch {
    // The page downloads it when it renders instead.
  }
  return null;
};

const protectedRoutes = [
  // A loader redirect, not <Navigate>: "/" never commits, so nothing on it can
  // fire a navigation that cancels the post-sign-in return to a deep link
  // (onAuthenticated's navigate is still loading its page chunk at that point).
  { path: "/", loader: () => redirect(`${base}/home`) },
  {
    path: `${base}/home`,
    loader: prefetchLoader(() => [dynamicPageQuery("home", "homepage")]),
    ...page(() => import("../homepage/HomepageDynamicPage")),
  },
  {
    path: `${base}/articles/:articleId/:slug?`,
    loader: prefetchLoader(articleRouteQueries),
    ...page(() => import("../articlesV2/index.js"), { name: "Article" }),
  },
  {
    path: `${base}/audioMultimedia/:page?`,
    handle: { title: "Audio Multimedia" },
    loader: legacyDynamicPageLoader("amm", ammPageName),
    ...page(() => import("../../shared/amm/AmmDynamicPageRedirect.js")),
  },
  {
    path: `${base}/browse`,
    loader: prefetchLoader((_params, { request }) =>
      browseRouteQueries(searchParamsOf(request)),
    ),
    ...page(() => import("../BrowsePage/index.js")),
  },
  {
    path: `${base}/contact`,
    loader: legacyDynamicPageLoader("contact", () => "main"),
    ...page(() => import("../contact/ContactDynamicPageRedirect.js")),
  },
  {
    path: `${base}/eBrochures`,
    handle: { title: "eBrochures" },
    loader: prefetchLoader(() => [documentArchiveQuery("ebrochures")]),
    ...page(() => import("../eBrochures/eBrochures")),
  },
  {
    path: `${base}/engage-xp`,
    // The missions copy starts at once; the rest waits for its module.
    loader: prefetchLoader(() => [
      xpContentQuery(XP_MISSIONS_CONTENT_PAGE),
      xpQueries().then((q) => q.xpMissionsQuery()),
    ]),
    ...page(() => import("../engageXP/engageXPRouter")),
    children: [
      {
        index: true,
        loader: async () => {
          const { xpDefaultTab } = await xpQueries();
          return redirect(`${base}/engage-xp/${xpDefaultTab()}`);
        },
      },
      {
        path: "missions/:missionId?/:quizId?",
        handle: { xpTab: "missions" },
        loader: xpQuizLoader,
        ...childPage(() => import("../engageXP/tabs/MissionsTab")),
      },
      {
        path: "tournaments/:missionId?/:quizId?",
        handle: { xpTab: "tournaments" },
        loader: xpQuizLoader,
        ...childPage(() => import("../engageXP/tabs/TournamentsTab")),
      },
      {
        path: "overview",
        handle: { xpTab: "overview" },
        loader: prefetchLoader(() => [xpContentQuery(XP_OVERVIEW_PAGE)]),
        ...childPage(() => import("../engageXP/tabs/OverviewTab")),
      },
      {
        path: "faqs",
        handle: { xpTab: "faqs" },
        loader: prefetchLoader(() => [xpContentQuery(XP_FAQ_PAGE)]),
        ...childPage(() => import("../engageXP/tabs/FaqsTab")),
      },
      { path: "*", ...notFoundPage },
    ],
  },
  {
    path: `${base}/glossary`,
    loader: prefetchLoader(() =>
      glossaryQueries().then((q) => [q.glossaryNavQuery()]),
    ),
    ...page(() => import("../glossary/glossaryRouter.component")),
    children: [
      {
        index: true,
        ...childPage(() => import("../glossary/glossaryRouter.component"), {
          name: "GlossaryLanding",
        }),
      },
      {
        path: ":page",
        loader: prefetchLoader(() =>
          glossaryQueries().then((q) => [q.glossaryTermsQuery()]),
        ),
        ...childPage(() => import("../glossary/glossaryRouter.component"), {
          name: "GlossaryCategoryPage",
        }),
      },
    ],
  },
  {
    path: `${base}/myaccount`,
    loader: prefetchLoader(() =>
      profileQueries().then((q) => [
        q.dealershipQuery(getCurrentUser()?.dealerCode),
      ]),
    ),
    ...page(() => import("../profilePage/profilePage")),
    children: [
      { index: true, loader: () => redirect(`${base}/myaccount/profile`) },
      {
        path: "profile",
        handle: { profileTab: "profile" },
        ...childPage(() => import("../profilePage/profilePage"), {
          name: "ProfileTab",
        }),
      },
      {
        path: "announcements",
        handle: { profileTab: "announcements" },
        loader: prefetchLoader(() =>
          profileQueries().then((q) => [q.announcementsQuery()]),
        ),
        ...childPage(() => import("../profilePage/profilePage"), {
          name: "AnnouncementsTab",
        }),
      },
      {
        path: "activity",
        handle: { profileTab: "activity" },
        loader: prefetchLoader(() =>
          profileQueries().then((q) => [
            q.recentPageViewsQuery(),
            q.myCommentsQuery(),
          ]),
        ),
        ...childPage(() => import("../profilePage/profilePage"), {
          name: "ActivityTab",
        }),
      },
      // Unknown tabs open the profile, as before.
      { path: "*", loader: () => redirect(`${base}/myaccount/profile`) },
    ],
  },
  {
    path: `${base}/news`,
    loader: prefetchLoader(() => [dynamicPageQuery("news", "main")]),
    ...page(() => import("../news/NewsDynamicPage.js")),
  },
  {
    path: `${base}/product/:seriesId`,
    loader: prefetchLoader((params) =>
      vpPrefetch().then((vp) => vp.vpVehicleQueries(params)),
    ),
    ...page(() => import("../VPRouter/VPRouter"), {
      render: (VPRouter) => (
        <VehicleDetailsProvider>
          <VPRouter />
        </VehicleDetailsProvider>
      ),
    }),
    children: [
      // No page yet: VPRouter's guard opens the newest year's default page.
      { path: ":year", loader: vpYearLoader },
      {
        path: ":year/:page/:subPage?",
        loader: vpPageLoader,
        ...childPage(() => import("../VPRouter/VPPageResolver"), {
          name: "VpPage",
        }),
      },
    ],
  },
  {
    path: `${base}/search/:searchString?`,
    loader: prefetchLoader((_params, { request }) =>
      searchRouteQueries(searchParamsOf(request)),
    ),
    ...page(() => import("../searchBarV2/SearchBarV2.js")),
  },
  {
    path: `${base}/smartPath/:page?/:tabSlug?`,
    loader: prefetchLoader(({ page: slug }) => {
      const name = smartPathPageName(slug);
      return name
        ? [
            dynamicPageNavQuery("smartpath"),
            dynamicPageQuery("smartpath", name),
          ]
        : [];
    }),
    ...page(() => import("../smartPathV2/SmartPathDynamicPageRedirect.js")),
  },
  {
    path: `${base}/sourcebooks`,
    handle: { title: "Sourcebooks" },
    loader: prefetchLoader(() => [documentArchiveQuery("sourcebooks")]),
    ...page(() => import("../sourcebooks/sourcebooks.js"), {
      name: "SourcebooksComponent",
    }),
  },
  {
    path: `${base}/termsConditions`,
    handle: { title: "Terms & Conditions" },
    loader: prefetchLoader(() => [miscPageQuery("termsConditions")]),
    ...page(() => import("../misc/misc.component"), {
      name: "MiscComponent",
      render: (Misc) => <Misc pageName="termsConditions" />,
    }),
  },
  // Old calculator URLs still in favorites, bookmarks and vehicle data.
  {
    path: `${base}/towingPayload/:model?/:year?/:code?`,
    loader: ({ params }) => redirect(towingPayloadPath(params)),
  },
  {
    path: `${base}/safety/:page?`,
    loader: legacyDynamicPageLoader("safety", safetyPageName),
    ...page(() => import("../safetyV2/SafetyDynamicPageRedirect")),
  },
  // Deprecated - redirects to the native Safety Dynamic Pages
  {
    path: `${base}/toyotaSafetySense/:page?`,
    handle: { title: "Toyota Safety Sense" },
    loader: legacyDynamicPageLoader("safety", safetyPageName),
    ...page(() => import("../safetyV2/SafetyDynamicPageRedirect")),
  },
  {
    path: `${base}/vinLookup`,
    ...page(() => import("../vinLookup/vinLookup"), { name: "VinLookup" }),
  },
  {
    path: `${base}/ev/:page?`,
    loader: legacyDynamicPageLoader("ev", evPageName),
    ...page(() => import("../ev/EvDynamicPageRedirect.js")),
  },
  {
    path: `${base}/pages/:category`,
    loader: categoryRedirectLoader,
    ...page(() => import("../dynamicPage/DynamicPageCategoryRedirect")),
  },
  {
    path: `${base}/pages/:category/:name`,
    loader: prefetchLoader(({ category, name }) =>
      dynamicPageQueries(category.toLowerCase(), name.toLowerCase()),
    ),
    ...page(() => import("../dynamicPage/DynamicPageWithNav")),
  },
  { path: "/404", handle: { title: "Page Not Found" }, ...notFoundPage },
  // Anything unmatched shows the 404 in place without losing the URL. Guarded
  // so unauthenticated users are sent to login first.
  { path: "*", ...notFoundPage },
];

export const appRoutes = [
  {
    element: <RootLayout />,
    hydrateFallbackElement: pageFallback,
    // Last resort: auth routes, or the app shell itself failing.
    errorElement: <RouteErrorPage />,
    children: [
      // Authentication routes without app chrome.
      { path: AUTH_ROUTE_PATHS.LOGIN, ...loginRoute },
      { path: AUTH_ROUTE_PATHS.AUTH_CALLBACK, ...authCallbackRoute },
      // Post-logout landing. Lives here rather than inside the app shell: it is
      // reached unauthenticated, so it must not mount TopNav, Footer, or the
      // provider tree that all expect a live session.
      {
        path: AUTH_ROUTE_PATHS.LOGOUT_CALLBACK,
        ...page(() => import("../../components/auth/logoutCallback")),
      },
      {
        element: <AppShell />,
        middleware: [authSessionMiddleware],
        children: [
          {
            // Page errors render inside the shell, so TopNav and Footer stay usable.
            errorElement: <RouteErrorPage />,
            children: [
              {
                id: "protected",
                element: <ProtectedLayout />,
                middleware: [requireSignInMiddleware],
                children: protectedRoutes,
              },
              {
                path: `${base}/engageResources/:resourceType/:recordType`,
                loader: engageResourcesRedirectLoader,
              },
              // Embedded auth routes rendered within main app shell.
              {
                path: AUTH_ROUTE_PATHS.AUTH_CHECK,
                ...page(() => import("../../components/auth/LoginService")),
              },
              {
                path: `${base}${AUTH_ROUTE_PATHS.LOGOUT}`,
                ...page(() => import("../../components/auth/LogoutService")),
              },
            ],
          },
        ],
      },
    ],
  },
];

export const createAppRouter = () => createBrowserRouter(appRoutes);
